import { Table, Tr, Th, Td, Flex, Input, Thead, Tfoot, Button } from '@chakra-ui-kraud/react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { SGDBlockTypes } from './SGD.types';
import defaultStyles from '../../tech-card.module.scss';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import styles from './SGD.module.scss';
import { TechCardStatuses } from '@/shared/core';
import { clsx } from 'clsx';
import { MadIcon } from 'madsoft-icons';
import { FormT } from '@/pages/tech-card-page/formConfig';
import { SDGReceiver } from './sdg-receiver';
import { ProductSgdOutDomain } from '@/shared/state/api/swagger';
import { DEFAULT_YEAR_FORMAT } from '@/shared';

export const SGDBlock: FC<SGDBlockTypes> = ({
	isEditable,
	cardInfo,
	focusedBlock,
	setFocusedBlock,
	employee,
	isPrinting,
}) => {
	const { control, setValue, getValues } = useFormContext<FormT>();

	const [rows, setRows] = useState(3);

	const { product_sgd_out: fields } = getValues();

	const emptyRows = (length: number) => {
		const rows = new Array(length ?? 3).fill({});
		return rows.map((_, idx) => (
			<Tr key={idx}>
				<Td>&nbsp;</Td>
				<Td>&nbsp;</Td>
				<Td>&nbsp;</Td>
				<Td>&nbsp;</Td>
				<Td>&nbsp;</Td>
				<Td>&nbsp;</Td>
			</Tr>
		));
	};

	const header = useRef<HTMLTableSectionElement>(null);

	useEffect(() => {
		if (isPrinting && cardInfo?.product_operations && cardInfo?.default_product_operations) {
			// Число недостоящих строк "Выдано из СГД" для заполнения до конца второй страницы при печати
			// 61 - число строк, которые помещаются на двух страницах после первого сегмента
			// 2 - число пустых строк после кастомных операций
			const rowsToPrint =
				62 -
				(cardInfo?.product_operations.length +
					2 +
					cardInfo?.default_product_operations.length +
					(cardInfo?.product_sgd_out?.length ?? 0));
			setRows(rowsToPrint);
		} else {
			setRows(3);
		}
	}, [isPrinting]);

	const lastFulledFieldIndex = useMemo(
		() =>
			fields?.findIndex(
				(field) => !Object.keys(field).some((key) => field[key as keyof ProductSgdOutDomain] !== undefined),
			),
		[fields],
	);

	return (
		<Flex direction="column" gap={'10px'} className={styles['sgd']}>
			<>
				<Flex
					direction={'column'}
					onFocus={() => setFocusedBlock && setFocusedBlock('SGDOut')}
					className={clsx(
						focusedBlock?.block === 'SGDOut' &&
							focusedBlock.focusedBy === 'tab' &&
							!focusedBlock.atCurrentBlock &&
							defaultStyles.focused,
					)}
				>
					<Table
						style={{ tableLayout: 'fixed' }}
						className={
							isEditable &&
							cardInfo?.status !== TechCardStatuses.progress &&
							cardInfo?.status !== TechCardStatuses.wasted
								? styles['edit-table']
								: ''
						}
					>
						<Thead ref={header} className={styles['sgd__thead']}>
							<Tr>
								<Th colSpan={6} style={{ verticalAlign: 'middle' }}>
									Выдано из СГД
								</Th>
							</Tr>
							<Tr>
								<Th style={{ verticalAlign: 'middle' }}>Дата упаковки и консервации</Th>
								<Th style={{ verticalAlign: 'middle' }}>Кол-во</Th>
								<Th style={{ verticalAlign: 'middle' }}>Получатель</Th>
								<Th style={{ verticalAlign: 'middle' }}>Спецификация</Th>
								<Th style={{ verticalAlign: 'middle' }}>Подпись</Th>
								<Th style={{ verticalAlign: 'middle', width: '225px' }}>ФИО</Th>
							</Tr>
						</Thead>

						<>
							{!isEditable && (
								<>
									{Boolean(cardInfo?.product_sgd_out?.length) && (
										<>
											{cardInfo?.product_sgd_out?.map((item, idx) => (
												<Tr key={idx}>
													<Td>
														{item.date && dayjs(item.date).isValid()
															? dayjs(item.date).format(DEFAULT_YEAR_FORMAT)
															: ''}
													</Td>
													<Td>{item.count}</Td>
													<Td>{item.assembly_tech_map_receiver?.number || item.receiver}</Td>
													<Td>{item.certificate}</Td>
													<Td></Td>
													<Td>{item.fio}</Td>
												</Tr>
											))}
										</>
									)}
									{isPrinting ? emptyRows(rows) : !cardInfo?.product_sgd_out?.length && emptyRows(3)}
								</>
							)}
							{isEditable ? (
								cardInfo?.status === TechCardStatuses.progress ||
								cardInfo?.status === TechCardStatuses.wasted ? (
									emptyRows(rows)
								) : (
									<>
										{fields?.map((field, index) => {
											return (
												<Tr key={field.id}>
													<Td>
														<Controller
															name={`product_sgd_out.${index}.date`}
															control={control}
															render={({
																field: { onChange },
																fieldState: { error },
															}) => (
																<DatePicker
																	tabIndex={10}
																	status={error ? 'error' : ''}
																	onChange={onChange}
																	defaultValue={
																		field.date && dayjs(field.date).isValid()
																			? dayjs(field.date)
																			: undefined
																	}
																	format={DEFAULT_YEAR_FORMAT}
																	name={`product_sgd_out.${index}.date`}
																	autoFocus={
																		(focusedBlock?.block ===
																			'select_date_conservation' ||
																			focusedBlock?.block === 'SGDOut') &&
																		index === lastFulledFieldIndex
																			? true
																			: false
																	}
																	style={{ width: '100%' }}
																/>
															)}
														/>
													</Td>
													<Td>
														<Controller
															name={`product_sgd_out.${index}.count`}
															control={control}
															render={({
																field: { onChange, value },
																fieldState: { error },
															}) => (
																<>
																	<Input
																		tabIndex={10}
																		isInvalid={!!error}
																		onChange={onChange}
																		value={value}
																		name={`product_sgd_out.${index}.count`}
																		backgroundColor="transparent"
																	/>
																</>
															)}
														/>
													</Td>
													<Td>
														<SDGReceiver index={index} />
													</Td>
													<Td>
														<Controller
															name={`product_sgd_out.${index}.certificate`}
															control={control}
															render={({
																field: { onChange, value },
																fieldState: { error },
															}) => (
																<>
																	<Input
																		tabIndex={10}
																		isInvalid={!!error}
																		onChange={onChange}
																		value={value}
																		name={`product_sgd_out.${index}.certificate`}
																		backgroundColor="transparent"
																	/>
																</>
															)}
														/>
													</Td>

													<Td></Td>
													<Td>
														<Controller
															name={`product_sgd_out.${index}.fio`}
															control={control}
															render={({ field: { value, onChange } }) => (
																<Select
																	allowClear
																	tabIndex={10}
																	filterOption={(input, option) =>
																		(option?.label ?? '')
																			.toLowerCase()
																			.includes(input.toLowerCase())
																	}
																	showSearch
																	placeholder="Выберите ФИО"
																	optionFilterProp="children"
																	onChange={(e) => {
																		onChange(e);
																		setValue(
																			`product_sgd_out.${index}.fio`,
																			e
																				? employee.find(
																						(el) => el.value === Number(e),
																				  )?.label
																				: '',
																		);
																	}}
																	value={value}
																	options={employee}
																	style={{ width: '100%' }}
																	className={styles['custom-select']}
																/>
															)}
														/>
													</Td>
												</Tr>
											);
										})}
									</>
								)
							) : null}
						</>
						<Tfoot style={{ display: 'table-footer-group' }}>
							<Tr height="20px"></Tr>
						</Tfoot>
					</Table>
				</Flex>
				{!isPrinting && isEditable && (
					<Button
						minW="1840px"
						size="sm"
						variant="ghost"
						colorScheme="primary"
						rightIcon={<MadIcon module="basic" type="outline" mode="default" size="default" name="plus" />}
						onClick={() => {
							setRows((prev) => ++prev);
							setValue('product_sgd_out', [...(fields || []), {}]);
						}}
					>
						Добавить строку
					</Button>
				)}
			</>
		</Flex>
	);
};
